/**
 * Error Reporting Repository
 *
 * Life-critical error reporting with FERP repository pattern compliance:
 * - Safety framework integration for all error operations
 * - Immutable audit trail for error tracking
 * - Tenant isolation for error reporting
 * - Life-critical error handling with emergency controls
 * - Repository pattern compliance (NO direct API calls)
 */

import { BaseRepository } from './BaseRepository';
// ID generation removed - backend generates all IDs per single execution path principle

export interface LifeCriticalError {
    id: string;
    type: 'PATIENT_SAFETY' | 'FINANCIAL_INTEGRITY' | 'LEGAL_PRIVILEGE' | 'SYSTEM_CRITICAL' | 'OPERATIONAL';
    severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'EMERGENCY';
    message: string;
    stack?: string;
    context: Record<string, unknown>;
    timestamp: number;
    tenantId: string;
    userId?: string;
    sessionId: string;
    environment: string;
    workflowContext?: {
        workflowId?: string;
        instanceId?: string;
        taskId?: string;
        executionId?: string;
    };
    safetyMetadata?: {
        affectsPatientSafety: boolean;
        affectsFinancialIntegrity: boolean;
        affectsLegalPrivilege: boolean;
        requiresEmergencyResponse: boolean;
        estimatedImpact: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    };
}

export interface ErrorReportingContext {
    operation: 'REPORT_ERROR' | 'LOG_VIOLATION' | 'EMERGENCY_ALERT' | 'BATCH_REPORT';
    tenantId: string;
    criticalityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    errorType?: string;
    affectedSystems?: string[];
}

export interface LogEntry {
    id: string;
    timestamp: number;
    level: 'debug' | 'info' | 'warn' | 'error' | 'critical';
    message: string;
    context?: Record<string, unknown>;
    error?: {
        name: string;
        message: string;
        stack?: string;
    };
    metadata: {
        url: string;
        userAgent: string;
        userId?: string;
        tenantId?: string;
        sessionId: string;
        buildVersion?: string;
        environment: string;
    };
    performance?: {
        duration?: number;
        memoryUsage?: number;
    };
}

export class ErrorReportingRepository extends BaseRepository<LifeCriticalError> {
    private emergencyControls: any;

    constructor() {
        super();
        this.initializeEmergencyControls();
    }

    private async initializeEmergencyControls(): Promise<void> {
        this.emergencyControls = {
            async activate(context: any): Promise<void> {
                console.error('EMERGENCY: Life-critical error detected - manual intervention required', context);
            }
        };
    }

    /**
     * Report life-critical error with MANDATORY safety validation
     */
    async reportLifeCriticalError(error: LifeCriticalError): Promise<void> {
        // MANDATORY: Safety validation even for error reporting
        const safetyContext: ErrorReportingContext = {
            operation: 'REPORT_ERROR',
            tenantId: error.tenantId,
            criticalityLevel: this.mapSeverityToCriticality(error.severity),
            errorType: error.type,
            affectedSystems: error.workflowContext ? ['workflow-engine'] : ['frontend']
        };

        const safetyResult = await this.validateRepositorySafety({
            operation: safetyContext.operation,
            domain: 'OPERATIONAL',
            tenantId: safetyContext.tenantId,
            criticalityLevel: safetyContext.criticalityLevel,
            metadata: { errorType: error.type }
        });

        // Even for error reporting, ensure safety validation passes
        if ((safetyResult as any)?.overall < 60) { // Lower threshold for error reporting
            console.warn(`Error reporting safety score low: ${(safetyResult as any)?.overall}`);
            // Continue with error reporting but log the safety concern
            await this.auditTrail?.logSafetyWarning?.({
                operation: 'ERROR_REPORTING',
                safetyScore: (safetyResult as any)?.overall,
                tenantId: error.tenantId,
                timestamp: Date.now()
            });
        }

        // Create audit context for error reporting
        const auditContext = {
            operation: `ERROR_REPORT_${error.type}`,
            timestamp: Date.now(),
            tenantId: error.tenantId,
            metadata: {
                errorType: error.type,
                severity: error.severity,
                safetyScore: (safetyResult as any)?.overall,
                sessionId: error.sessionId
            },
            safetyScore: (safetyResult as any)?.overall
        };

        // Repository pattern: Store error through mutation with audit
        await this.executeWithAudit(auditContext, async () => {
            return this.mutation(
                `
                mutation ReportLifeCriticalError($error: LifeCriticalErrorInput!, $tenantId: String!) {
                    reportLifeCriticalError(error: $error, tenantId: $tenantId) {
                        id
                        reportedAt
                        auditId
                        emergencyControlsTriggered
                        notificationsSent
                        safetyAssessment {
                            score
                            violations
                            blockedOperations
                        }
                    }
                }
                `,
                {
                    error: this.sanitizeErrorForTransmission(error),
                    tenantId: error.tenantId
                }
            );
        });

        // Emergency controls for critical errors
        if (error.severity === 'CRITICAL' || error.severity === 'EMERGENCY') {
            await this.triggerEmergencyControls(error);
        }
    }

    /**
     * Log entries through repository pattern (replaces direct fetch in Logger.ts)
     */
    async logEntries(entries: LogEntry[], tenantId: string): Promise<void> {
        const safetyContext: ErrorReportingContext = {
            operation: 'BATCH_REPORT',
            tenantId,
            criticalityLevel: this.determineBatchCriticality(entries)
        };

        await this.validateRepositorySafety({
            operation: safetyContext.operation,
            domain: 'OPERATIONAL',
            tenantId: safetyContext.tenantId,
            criticalityLevel: safetyContext.criticalityLevel
        });

        // Repository pattern: Store log entries through mutation
        return this.mutation(
            `
            mutation LogEntries($entries: [LogEntryInput!]!, $tenantId: String!) {
                logEntries(entries: $entries, tenantId: $tenantId) {
                    success
                    entriesProcessed
                    auditId
                    timestamp
                }
            }
            `,
            {
                entries: entries.map(entry => this.sanitizeLogEntry(entry)),
                tenantId
            }
        );
    }

    /**
     * Report safety violations through repository
     */
    async reportSafetyViolation(violation: any): Promise<void> {
        const safetyContext: ErrorReportingContext = {
            operation: 'LOG_VIOLATION',
            tenantId: violation.tenantId || this.tenant,
            criticalityLevel: 'HIGH'
        };

        await this.validateRepositorySafety({
            operation: safetyContext.operation,
            domain: 'OPERATIONAL',
            tenantId: violation.tenantId || this.tenant,
            criticalityLevel: safetyContext.criticalityLevel
        });

        return this.mutation(
            `
            mutation ReportSafetyViolation($violation: SafetyViolationInput!, $tenantId: String!) {
                reportSafetyViolation(violation: $violation, tenantId: $tenantId) {
                    id
                    reportedAt
                    emergencyControlsActivated
                    auditTrailCreated
                }
            }
            `,
            {
                violation,
                tenantId: violation.tenantId || this.tenant
            }
        );
    }

    /**
     * Get error reports by tenant and filters
     */
    async findErrorsByTenant(
        tenantId: string,
        filters?: {
            severity?: string;
            type?: string;
            dateRange?: { start: Date; end: Date };
            limit?: number;
        }
    ): Promise<LifeCriticalError[]> {
        const safetyContext: ErrorReportingContext = {
            operation: 'REPORT_ERROR',
            tenantId,
            criticalityLevel: 'MEDIUM'
        };

        await this.validateRepositorySafety({
            operation: safetyContext.operation,
            domain: 'OPERATIONAL',
            tenantId,
            criticalityLevel: safetyContext.criticalityLevel
        });

        return this.query(
            `
            query GetErrorsByTenant(
                $tenantId: String!
                $filters: ErrorFiltersInput
            ) {
                errorsByTenant(tenantId: $tenantId, filters: $filters) {
                    id
                    type
                    severity
                    message
                    timestamp
                    context
                    workflowContext {
                        workflowId
                        instanceId
                        taskId
                    }
                    safetyMetadata {
                        affectsPatientSafety
                        affectsFinancialIntegrity
                        requiresEmergencyResponse
                    }
                }
            }
            `,
            { tenantId, filters }
        );
    }

    // Private helper methods

    // Use BaseRepository.validateRepositorySafety

    private async executeWithAudit<T>(
        auditContext: any,
        operation: () => Promise<T>
    ): Promise<T> {
        await this.auditTrail.logOperationStart(auditContext);

        try {
            const result = await operation();

            await this.auditTrail.logOperationSuccess({
                ...auditContext,
                result: 'SUCCESS',
                completedAt: Date.now()
            });

            return result;
        } catch (error) {
            await this.auditTrail.logOperationFailure({
                ...auditContext,
                error: (error as any)?.message ?? String(error),
                failedAt: Date.now()
            });

            throw error as Error;
        }
    }

    private async triggerEmergencyControls(error: LifeCriticalError): Promise<void> {
        if (!this.emergencyControls) {
            console.error('Emergency controls not initialized');
            return;
        }

        await this.emergencyControls.activate({
            trigger: 'CRITICAL_ERROR',
            errorId: error.id,
            tenantId: error.tenantId,
            severity: error.severity,
            affectedSystems: error.safetyMetadata?.affectsPatientSafety ? ['patient-safety'] : ['operational']
        });
    }

    private mapSeverityToCriticality(severity: string): 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' {
        switch (severity) {
            case 'LOW': return 'LOW';
            case 'MEDIUM': return 'MEDIUM';
            case 'HIGH':
            case 'CRITICAL': return 'HIGH';
            case 'EMERGENCY': return 'CRITICAL';
            default: return 'MEDIUM';
        }
    }

    private determineBatchCriticality(entries: LogEntry[]): 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' {
        const hasError = entries.some(entry => entry.level === 'error' || entry.level === 'critical');
        const hasCritical = entries.some(entry => entry.level === 'critical');

        if (hasCritical) return 'CRITICAL';
        if (hasError) return 'HIGH';
        if (entries.length > 100) return 'MEDIUM';
        return 'LOW';
    }

    private sanitizeErrorForTransmission(error: LifeCriticalError): any {
        // Remove sensitive data before transmission
        const sanitized = { ...error };

        // Remove sensitive context data
        if (sanitized.context) {
            sanitized.context = this.sanitizeContext(sanitized.context);
        }

        // Truncate stack traces if too long
        if (sanitized.stack && sanitized.stack.length > 10000) {
            sanitized.stack = sanitized.stack.substring(0, 10000) + '... [truncated]';
        }

        return sanitized;
    }

    private sanitizeLogEntry(entry: LogEntry): any {
        const sanitized = { ...entry };

        // Remove sensitive metadata
        if (sanitized.context) {
            sanitized.context = this.sanitizeContext(sanitized.context);
        }

        // Ensure user agent is sanitized
        if (sanitized.metadata.userAgent) {
            sanitized.metadata.userAgent = this.sanitizeUserAgent(sanitized.metadata.userAgent);
        }

        return sanitized;
    }

    private sanitizeContext(context: Record<string, unknown>): Record<string, unknown> {
        const sanitized: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(context)) {
            // Skip sensitive keys
            if (this.isSensitiveKey(key)) {
                sanitized[key] = '[REDACTED]';
                continue;
            }

            // Sanitize nested objects
            if (typeof value === 'object' && value !== null) {
                sanitized[key] = this.sanitizeContext(value as Record<string, unknown>);
            } else {
                sanitized[key] = value;
            }
        }

        return sanitized;
    }

    private isSensitiveKey(key: string): boolean {
        const sensitiveKeys = [
            'password', 'token', 'secret', 'key', 'auth', 'credential',
            'ssn', 'social', 'dob', 'email', 'phone', 'address',
            'medical', 'diagnosis', 'treatment', 'patient'
        ];

        return sensitiveKeys.some(sensitive =>
            key.toLowerCase().includes(sensitive.toLowerCase())
        );
    }

    private sanitizeUserAgent(userAgent: string): string {
        // Remove potentially identifying information from user agent
        return userAgent.replace(/\([^)]*\)/g, '(...)');
    }

    // ID generation removed - backend generates all IDs

    // Repository pattern standard methods

    async findByTenantAndId(tenantId: string, errorId: string): Promise<LifeCriticalError | null> {
        const safetyContext: ErrorReportingContext = {
            operation: 'REPORT_ERROR',
            tenantId,
            criticalityLevel: 'MEDIUM'
        };

        await this.validateRepositorySafety({
            operation: safetyContext.operation,
            domain: 'OPERATIONAL',
            tenantId,
            criticalityLevel: safetyContext.criticalityLevel
        });

        return this.query(
            `
            query GetErrorById($tenantId: String!, $errorId: String!) {
                errorById(tenantId: $tenantId, id: $errorId) {
                    id
                    type
                    severity
                    message
                    stack
                    context
                    timestamp
                    tenantId
                    userId
                    sessionId
                    environment
                    workflowContext {
                        workflowId
                        instanceId
                        taskId
                        executionId
                    }
                    safetyMetadata {
                        affectsPatientSafety
                        affectsFinancialIntegrity
                        affectsLegalPrivilege
                        requiresEmergencyResponse
                        estimatedImpact
                    }
                }
            }
            `,
            { tenantId, errorId }
        );
    }

    async create(error: Partial<LifeCriticalError>): Promise<LifeCriticalError> {
        // Create error through backend mutation - backend generates ID
        const errorData = {
            type: error.type || 'OPERATIONAL',
            severity: error.severity || 'MEDIUM',
            message: error.message || 'Unknown error',
            stack: error.stack,
            context: error.context || {},
            timestamp: Date.now(),
            tenantId: error.tenantId || this.tenant,
            userId: error.userId,
            sessionId: error.sessionId || 'unknown',
            environment: error.environment || 'production',
            workflowContext: error.workflowContext,
            safetyMetadata: error.safetyMetadata || {
                affectsPatientSafety: false,
                affectsFinancialIntegrity: false,
                affectsLegalPrivilege: false,
                requiresEmergencyResponse: false,
                estimatedImpact: 'LOW'
            }
        };

        // Use saveDraft mutation to create error record with backend-generated ID
        const result = await this.mutation(
            `
            mutation SaveErrorReport($input: SaveDraftInput!) {
                saveDraft(input: $input) {
                    ok
                    key
                    message
                }
            }
            `,
            {
                input: {
                    tenant: errorData.tenantId,
                    kind: 'ErrorReport',
                    name: `error-${errorData.type.toLowerCase()}-${Date.now()}`,
                    key: `error-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
                    draft: JSON.stringify(errorData)
                }
            }
        );

        if (!result.saveDraft.ok) {
            throw new Error(`Failed to create error report: ${result.saveDraft.message}`);
        }

        // Return the error with backend-generated ID (key becomes ID)
        return {
            ...errorData,
            id: result.saveDraft.key,
            ...error // Preserve any additional fields
        } as LifeCriticalError;
    }
}

// Singleton instance for error reporting
export const errorReportingRepository = new ErrorReportingRepository();

/**
 * Mock implementation for development and testing
 * Provides same interface as production repository but with simulated backend
 */
export class MockErrorReportingRepository extends ErrorReportingRepository {
    private mockErrors: LifeCriticalError[] = [];

    async reportLifeCriticalError(error: LifeCriticalError): Promise<void> {
        console.log('[Mock] Reporting life-critical error:', error);
        this.mockErrors.push(error);

        // Simulate async operation
        await new Promise(resolve => setTimeout(resolve, 100));

        // Simulate emergency controls for critical errors
        if (error.severity === 'EMERGENCY' || error.severity === 'CRITICAL') {
            console.error('[Mock] EMERGENCY ALERT:', error.message);
        }
    }

    async reportBatchLogs(entries: LogEntry[]): Promise<void> {
        console.log('[Mock] Batch logging:', entries.length, 'entries');

        // Convert log entries to errors for storage
        for (const entry of entries) {
            if (entry.level === 'error' || entry.level === 'critical') {
                // Use the parent create() method which now uses backend pattern
                const error = await this.create({
                    type: 'OPERATIONAL',
                    severity: entry.level === 'critical' ? 'CRITICAL' : 'HIGH',
                    message: entry.message,
                    context: entry.context,
                    timestamp: entry.timestamp,
                    tenantId: this.tenant,
                    sessionId: 'mock-session',
                    environment: 'development'
                });
                this.mockErrors.push(error);
            }
        }
    }

    async getErrorsByTenant(
        tenantId: string,
        filters?: {
            severity?: string[];
            type?: string[];
            dateRange?: { start: Date; end: Date };
            limit?: number;
        }
    ): Promise<LifeCriticalError[]> {
        console.log('[Mock] Getting errors for tenant:', tenantId);

        let filtered = this.mockErrors.filter(e => e.tenantId === tenantId);

        if (filters?.severity) {
            filtered = filtered.filter(e => filters.severity!.includes(e.severity));
        }

        if (filters?.type) {
            filtered = filtered.filter(e => filters.type!.includes(e.type));
        }

        if (filters?.dateRange) {
            const start = filters.dateRange.start.getTime();
            const end = filters.dateRange.end.getTime();
            filtered = filtered.filter(e => e.timestamp >= start && e.timestamp <= end);
        }

        if (filters?.limit) {
            filtered = filtered.slice(0, filters.limit);
        }

        return filtered;
    }

    async findByTenantAndId(tenantId: string, errorId: string): Promise<LifeCriticalError | null> {
        console.log('[Mock] Finding error by ID:', errorId);
        return this.mockErrors.find(e => e.tenantId === tenantId && e.id === errorId) || null;
    }

    // Mock-specific helper to clear errors for testing
    clearMockErrors(): void {
        this.mockErrors = [];
    }
}